import { AppConstants } from './../../app.constants';
import { Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Account } from '../../models/account';
import { NostosService } from '../../services/nostos.service';
import { debounceTime, filter, finalize, switchMap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInput, MatLabel } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { OptionDialogComponent } from '../../dialogs/options/options-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Asset } from '../../models/asset';
import { DialogOption } from '../../models/dialogoption';
import { AlertSettings } from '../../models/alertsettings';
import { LoadingSpinnerDialogComponent } from '../../dialogs/loading-spinner/loading-spinner-dialog.component';
import { Confirmation } from '../../models/confirmation';
import { ConfirmationDialogComponent } from '../../dialogs/confirmation/confirmation-dialog.component';

@Component({
  selector: 'app-customer',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatExpansionModule, MatLabel, MatInput, MatIconModule, MatListModule, MatTooltipModule],
  templateUrl: './customer.component.html',
  styleUrl: './customer.component.css'
})
export class CustomerComponent {

  searchControl = new FormControl('');
  accounts: Account[] = [];
  loadingAccountId: string | null = null;

  constructor(private nostosService: NostosService, private dialog: MatDialog) { }

  ngOnInit() {
    this.searchControl.valueChanges.pipe(
      filter(value => (value ?? '').length >= 3),
      debounceTime(300), // wait 300ms after last keystroke
      switchMap(value => this.nostosService.searchAccounts(value ?? ''))
    ).subscribe(results => {
      this.accounts = results;
    });
  }

  loadAssets(account: Account, force: boolean) {

    if (!force) {
      if (account.assets && account.assets.length > 0) {
        // Already loaded
        return;
      }
    }

    console.log(`loading asset with account: {account.name}`);

    const dialogRef = this.dialog.open(LoadingSpinnerDialogComponent, {
      disableClose: true,
      panelClass: 'loading-dialog'
    });

    this.nostosService.getAssetsForAccount(account.id).pipe(finalize(() => { dialogRef.close(); })).subscribe({
      next: assets => {
        account.assets = assets;
      }
    });

  }

  isOld(utcString: string): boolean {

    if (!utcString) return false;
    const ts = new Date(utcString.endsWith("Z") ? utcString : utcString + "Z").getTime();
    const now = Date.now();
    return (now - ts) > 5 * 60 * 1000;

  }

  getSince(utcString: string): string {

    const past = new Date(utcString.endsWith("Z") ? utcString : utcString + "Z");
    const now = new Date();

    var diff = Math.floor((now.getTime() - past.getTime()) / 1000); // total seconds
    const days = Math.floor(diff / (24 * 3600));
    diff %= 24 * 3600;
    const hours = Math.floor(diff / 3600);
    diff %= 3600;
    const minutes = Math.floor(diff / 60);
    const seconds = diff % 60;

    var result = `${days} day(s), ${hours} hour(s), ${minutes} minutes(s), ${seconds} second(s)`;
    return result;

  }

  openCharts(asset: Asset) {

    window.open(this.nostosService.getChartsUrl(asset.hostId), '_blank'); // opens in a new tab

  }

  openProblems(asset: Asset) {

    window.open(this.nostosService.getProblemsUrl(asset.hostId), '_blank'); // opens in a new tab

  }

  createMonitor(account: Account, asset: Asset) {

    const dialogRef = this.dialog.open(LoadingSpinnerDialogComponent, {
      disableClose: true,
      panelClass: 'loading-dialog'
    });

    this.nostosService.createMonitor(asset.id).pipe(finalize(() => { dialogRef.close(); })).subscribe({
      next: (data) => {
        this.loadAssets(account, true);
      }
    });

  }

  deleteMonitor(account: Account, asset: Asset) {

    var theConfirm: Confirmation = {
      title: "Confirm Delete",
      message: "Really delete the monitoring from Zabbix?",
      isOkay: false
    };

    const confirmRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        confirmation: theConfirm
      },
    });

    confirmRef.afterClosed().subscribe((selection: Confirmation | undefined) => {

      if (selection) {
        const dialogRef = this.dialog.open(LoadingSpinnerDialogComponent, {
          disableClose: true,   // user can't close it manually
          panelClass: 'loading-dialog'
        });
        this.nostosService.deleteMonitor(asset.id).pipe(finalize(() => { dialogRef.close(); })).subscribe({
          next: (data) => {
            this.loadAssets(account, true);
          }
        });
      }

    });

  }

  getAssetTooltip(asset: Asset): string {
    return [
      `Last Sync: ${asset.lastSyncTs}`,
      `Since: ${this.getSince(asset.lastSyncTs)}`,
      `OS Version: ${asset.osVersion}`,
      `Steward Version: ${asset.stewardVersion}`,
      `Plugin Version: ${asset.pluginVersion}`,
    ].join('\n');
  }

  setAlerts(account: Account, asset: Asset) {

    var theOptions: DialogOption[] = [
      { name: "ThreadsAverageNo", label: "Threads Avg (#)", selected: false, lessThan: false, alertType: AppConstants.ALERTTYPE_STANDARD, idleWindow: "" },
      { name: "CPUAveragePct", label: "CPU Avg (%)", selected: false, lessThan: false, alertType: AppConstants.ALERTTYPE_STANDARD, idleWindow: "" },
      { name: "FreeMemAverageMB", label: "Free Mem Avg (MB)", selected: false, lessThan: false, alertType: AppConstants.ALERTTYPE_STANDARD, idleWindow: "" },
      { name: "ProcessMemAverageMB", label: "Process Mem Avg (MB)", selected: false, lessThan: false, alertType: AppConstants.ALERTTYPE_STANDARD, idleWindow: "" },
      { name: "DiskUsedPct", label: "Disk Used (%)", selected: false, lessThan: false, alertType: AppConstants.ALERTTYPE_STANDARD, idleWindow: "" },
      { name: "NumErrors", label: "Errors (#)", selected: false, lessThan: false, alertType: AppConstants.ALERTTYPE_STANDARD, idleWindow: "" },
      { name: "NumWarns", label: "Warnings (#)", selected: false, lessThan: false, alertType: AppConstants.ALERTTYPE_STANDARD, idleWindow: "" }
    ];

    // throw up the spinner

    const spinnerRef = this.dialog.open(LoadingSpinnerDialogComponent, {
      disableClose: true,   // user can't close it manually
      panelClass: 'loading-dialog'
    });

    // fetch any custom attributes

    this.nostosService.getCustomStats(asset.id).subscribe(counters => {

      theOptions = theOptions.concat(counters.map(o => ({ name: o.name, label: o.name, selected: false, lessThan: false, alertType: o.alertType })))

      // fetch the existing values, if any

      this.nostosService.getAlerts(asset.id).pipe(finalize(() => { spinnerRef.close(); })).subscribe(x => {

        for (var t of x.alerts) {
          var option = theOptions.find(o => o.name == t.name);
          if (option) {
            option.selected = true;
            option.inputValue = t.value;
            option.lessThan = t.lessThan;
            option.idleWindow = t.idleWindow;
          } else {
            console.log("warn: unable to matching option for trigger: " + t.name);
          }
        }

        const dialogRef = this.dialog.open(OptionDialogComponent, {
          minWidth: '600px',
          data: {
            options: theOptions
          },
        });

        dialogRef.afterClosed().subscribe((selectedOptions: DialogOption[] | undefined) => {

          if (selectedOptions) {

            console.log('User selected:', selectedOptions);

            const theSettings: AlertSettings = {
              masterItemId: asset.masterItemId,
              alerts: selectedOptions.map(o => ({ name: o.name, value: String(o.inputValue), lessThan: o.lessThan, alertType: o.alertType, idleWindow: String(o.idleWindow)}))
            };

            const dialogRef = this.dialog.open(LoadingSpinnerDialogComponent, {
              disableClose: true,
              panelClass: 'loading-dialog'
            });

            console.log("the alerts:", theSettings);

            this.nostosService.setAlerts(asset.id, theSettings).pipe(finalize(() => { dialogRef.close(); })).subscribe({
              next: (data) => {
                this.loadAssets(account, true);
              }
            });

          }

        });

      });

    });

  }

  downloadZip(name: string, assetId: string) {

    // throw up the spinner

    const spinnerRef = this.dialog.open(LoadingSpinnerDialogComponent, {
      disableClose: true,   // user can't close it manually
      panelClass: 'loading-dialog'
    });

    // Do whatever you need with the selections

    this.nostosService.getAssetZip(assetId).pipe(finalize(() => { spinnerRef.close(); })).subscribe(response => {

      const blob = response.body as Blob;
      const a = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;

      // Try to get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition') || response.headers.get('content-disposition');
      let filename: string | null = null;
      if (contentDisposition) {
        // common header formats: attachment; filename="name.zip" or attachment; filename=name.zip
        const match = /filename\*=UTF-8''([^;\n\r]+)|filename="?([^";\n\r]+)"?/i.exec(contentDisposition);
        if (match) {
          filename = decodeURIComponent(match[1] || match[2]);
        }
      }

      if (!filename) {
        // fallback to previous behavior
        filename = `${name}-${assetId}.zip`;
      }

      a.download = filename;
      a.click();
      URL.revokeObjectURL(objectUrl);

    });

  }

}
